import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { hasClearedAppropriatenessTest } from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TestAnswersDto } from '@model/ClientTestAnswerDto'
import {
  TickmillProductType,
  isTickmillProductTypeCFD,
  isTickmillProductTypeETD,
} from '@model/TickmillProductType'

import { useLandingPage } from '@hooks/useLandingPage'

import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { TestTemplate } from '@shared/templates/TestTemplate'

import { ApTestRiskText } from './ApTestRiskText'
import { useResolvedAppTestProduct } from './hooks/useResolvedAppTestProduct'

export const ProductAppropriatenessTestRiskPage: React.FC = () => {
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { refreshAccount } = useAccountWriteContext()
  const [answers, setAnswers] = useState<TestAnswersDto | undefined>()
  const [isLoading, setLoading] = useState<boolean>(true)
  const { t } = useTranslation()
  const contentRef = useRef<HTMLDivElement>(null)
  const resolvedProductId = useResolvedAppTestProduct()
  const { getLandingPage } = useLandingPage()
  const isSQFProduct = resolvedProductId === TickmillProductType.SQF

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const a = await apiClient.getAccountAppTestAnswers(locale, resolvedProductId)
        if (hasClearedAppropriatenessTest(account, resolvedProductId)) return navigate('../success')
        if (!cancelled) setAnswers(a)
      } finally {
        if (!hasClearedAppropriatenessTest(account, resolvedProductId)) setLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [account, apiClient, locale, navigate, resolvedProductId])

  const handleAccept = async () => {
    if (!account?.id || !answers?.id) return
    await apiClient.acceptAppTestRisk({
      clientId: account.id,
      clientTestId: answers.id,
      riskAccepted: true,
    })
    await refreshAccount(locale)
    navigate('../success')
  }

  const handleDecline = async () => {
    if (account?.id && answers?.id) {
      await apiClient.acceptAppTestRisk({
        clientId: account.id,
        clientTestId: answers.id,
        riskAccepted: false,
      })
    }
    if (isSQFProduct) {
      await refreshAccount(locale)
      navigate('../risk-declined')
      return
    }
    if (
      isTickmillProductTypeCFD(resolvedProductId) ||
      isTickmillProductTypeETD(resolvedProductId)
    ) {
      navigate('/logout')
    } else {
      await refreshAccount(locale)
      navigate(getLandingPage())
    }
  }

  return (
    <TestTemplate maxWidth={600} isLoading={isLoading} contentRef={contentRef}>
      <ContentBlock
        variant='voucher'
        icon={<FeatureIcon size='lg' icon={WarningIcon} bg='surface-warning-high-em' />}
        title={isSQFProduct ? t('SQF.riskAcceptance.header') : t('Sign up.Proceed with caution')}
        description={<ApTestRiskText product={resolvedProductId} />}
      />
      <FullScreenFooter contentRef={contentRef}>
        <Button.Primary fullWidth size='lg' onClick={handleAccept} disabled={isLoading}>
          {isSQFProduct ? t('SQF.riskAcceptance.acceptCta') : t('Yes I accept the Risk')}
        </Button.Primary>
        <Button.Secondary fullWidth size='lg' onClick={handleDecline} disabled={isLoading}>
          {isSQFProduct ? t('SQF.riskAcceptance.declineCta') : t('No I do not accept the Risk')}
        </Button.Secondary>
      </FullScreenFooter>
    </TestTemplate>
  )
}
